import GoogleGeocoder = google.maps.Geocoder;
import {inject, injectable} from '@123/druid/dist/Framework/Decorators/Inject';
import type {PickupLocation} from '@Component/PickupLocator/Schema/PickupLocation';
import Config from '@Service/Config/Config';

@injectable()
export default class Geocoder {
    private geocoder?: GoogleGeocoder;

    constructor(@inject(Config) private readonly config: Config = new Config()) {}

    public async setup(): Promise<void> {
        try {
            const {setOptions, importLibrary} = await import('@googlemaps/js-api-loader');

            setOptions({key: this.config.getConfig('config.google-maps-api-key')});
            const geocodingLibrary = await importLibrary('geocoding');
            this.geocoder = new geocodingLibrary.Geocoder();
        } catch (e) {
            console.error('Failed to setup Google Autocomplete', e);
        }
    }

    public provide(address: string): Promise<PickupLocation> {
        return new Promise((resolve, reject) => {
            if (this.geocoder === undefined) {
                reject(new Error('Geocoder has not been set up'));
                return;
            }

            this.geocoder
                .geocode({address}, (result, status) => {
                    if (status === 'OK' && result?.[0] !== undefined) {
                        resolve({
                            lat: result[0].geometry.location.lat(),
                            lng: result[0].geometry.location.lng(),
                            country: this.getCountry(result[0]),
                            icon: null,
                            id: null,
                        });
                    }

                    reject(new Error('Failed to geocode address'));
                })
                .then(() => {})
                .catch(() => {});
        });
    }

    private getCountry(geometry: google.maps.GeocoderResult | google.maps.places.PlaceResult): string | null {
        if (geometry.address_components === undefined) {
            return null;
        }

        return geometry.address_components.find((component) => component.types.includes('country'))?.short_name ?? null;
    }
}
